import React, { forwardRef, useId, useMemo } from "react";
import { FormGroup } from "react-bootstrap";
import {
  FormLabel,
  FormFeedback,
  DropIcon,
  LoadingMessage,
  NoOptionsMessage,
  ClearIndicator,
  MenuList,
  MultiValueRemove,
} from "./utils";

import AsyncCreatableSelect from "react-select/async-creatable";
import AsyncSelect from "react-select/async";
import CreatableSelect from "react-select/creatable";
import Select from "react-select";

/* ----------------------------------------------
   Default Styles & Classes
---------------------------------------------- */
const customStyle = {
  control: (base, state = {}) => {
    const isMultiValue = !!state.isMulti && state.getValue?.().length > 0;
    return {
      ...base,
      minHeight: 32,
      minWidth: 54,
      padding: isMultiValue ? "10px 10px" : "12px 10px",
    };
  },
  menuPortal: (base) => ({
    ...base,
    zIndex: 200,
  }),
  loadingIndicator: (base) => ({
    ...base,
    color: "#213B73",
    padding: "0 6px",
  }),
  menuList: (base) => ({
    ...base,
    maxHeight: "160px",
  }),
};

const customClasses = {
  control: (state) =>
    [
      "rounded-4 shadow-none form-control d-flex gap-5",
      state.isFocused || state.menuIsOpen ? "border-primary" : "border",
      state.isDisabled ? "disabled" : "cursor-pointer",
    ].join(" "),
  menu: () => "bg-white border shadow-sm p-1 z-200 p-4 pe-2 rounded-3",
  menuList: () => "p-0 pe-2 position-relative custom-scroll",
  option: (state) =>
    [
      "text-start fs-sm px-5 py-4 rounded-2 cursor-pointer select-option",
      state.isSelected ? "active" : "",
      state.isFocused ? "focused" : "",
    ].join(" "),
  placeholder: () => "text-base text-secondary",
  valueContainer: ({ isMulti, hasValue }) =>
    `p-0 m-0 ${isMulti && hasValue ? "d-flex gap-2" : ""}`,
  singleValue: () => "m-0",
  multiValue: ({ isMulti }) =>
    `bg-primary-10 primary-text rounded-2 px-2 py-1 gap-2 m-0 border border-primary-20 align-items-center ${isMulti ? "flex-wrap" : "flex-nowrap"}`,
  multiValueLabel: () => "primary-text fs-xs fw-medium p-0",
  multiValueRemove: () => "p-0 text-danger bg-transparent transition-all",
  indicatorSeparator: () => "d-none",
  clearIndicator: () => "p-0",
  dropdownIndicator: () => "p-0",
  indicatorsContainer: () => "p-0 gap-2",
  input: () => "m-0 p-0",
  group: () => "p-0",
  groupHeading: () =>
    "text-secondary fw-bold fs-xs text-uppercase px-5 py-2 bg-light border-bottom mb-1",
};

/* ----------------------------------------------
   Select Component Resolver (moved outside)
---------------------------------------------- */
const SelectComponent = React.memo((props) => {
  const { loadOptions, isCreatable } = props;
  let Component;

  if (loadOptions) {
    Component = isCreatable ? AsyncCreatableSelect : AsyncSelect;
  } else {
    Component = isCreatable ? CreatableSelect : Select;
  }

  return <Component {...props} />;
});

/* ----------------------------------------------
   Reorder List helper
---------------------------------------------- */
const reorderList = (list, value) => {
  let baseList = list ? [...list] : [];

  // Ensure the selected value(s) are present in the list
  if (value) {
    const selectedItems = Array.isArray(value) ? value : [value];
    selectedItems.forEach((val) => {
      if (val && val.value !== undefined) {
        // Check if this selected item is already in the list
        let exists = false;
        baseList.forEach((opt) => {
          if (!opt) return;
          if (Array.isArray(opt.options)) {
            if (opt.options.some((subOpt) => subOpt && subOpt.value === val.value)) {
              exists = true;
            }
          } else {
            if (opt.value === val.value) {
              exists = true;
            }
          }
        });

        // If it doesn't exist, insert it
        if (!exists) {
          // Check if it's a grouped list and we can find a matching group
          let addedToGroup = false;
          const categoryName = val.category || val.data?.category || val.category_name;
          if (categoryName && typeof categoryName === "string") {
            baseList = baseList.map((opt) => {
              if (opt && Array.isArray(opt.options)) {
                if (String(opt.label).toLowerCase() === categoryName.toLowerCase()) {
                  addedToGroup = true;
                  return {
                    ...opt,
                    options: [val, ...opt.options],
                  };
                }
              }
              return opt;
            });
          }
          // If it wasn't added to a group (or list is not grouped), prepend it at the root
          if (!addedToGroup) {
            baseList = [val, ...baseList];
          }
        }
      }
    });
  }

  if (baseList.length === 0 || !value) return baseList;

  let selectedValues = [];
  if (Array.isArray(value)) {
    selectedValues = value.map((val) => (val && val.value !== undefined ? val.value : val));
  } else if (value) {
    selectedValues = [value.value !== undefined ? value.value : value];
  }
  selectedValues = selectedValues.filter((v) => v !== null && v !== undefined);
  if (selectedValues.length === 0) return baseList;

  const isSelected = (opt) => {
    const optValue = opt && opt.value !== undefined ? opt.value : opt;
    return selectedValues.includes(optValue);
  };

  const selectedPart = [];
  const nonSelectedPart = [];

  baseList.forEach((opt) => {
    if (!opt) return;

    if (Array.isArray(opt.options)) {
      const groupSelected = [];
      const groupNonSelected = [];

      opt.options.forEach((subOpt) => {
        if (isSelected(subOpt)) {
          groupSelected.push(subOpt);
        } else {
          groupNonSelected.push(subOpt);
        }
      });

      if (groupSelected.length > 0 || groupNonSelected.length > 0) {
        const reorderedGroupOptions = [...groupSelected, ...groupNonSelected];
        if (groupSelected.length > 0) {
          selectedPart.push({
            ...opt,
            options: reorderedGroupOptions,
          });
        } else {
          nonSelectedPart.push({
            ...opt,
            options: reorderedGroupOptions,
          });
        }
      }
    } else {
      if (isSelected(opt)) {
        selectedPart.push(opt);
      } else {
        nonSelectedPart.push(opt);
      }
    }
  });

  return [...selectedPart, ...nonSelectedPart];
};

/* ----------------------------------------------
   Main Component
---------------------------------------------- */
const CustomSelect = (
  {
    isDisabled = false,
    className = "",
    coverClass = "",
    isHorizontal = false,
    style = {},
    label = {},
    validation = {},
    selectProps = {},
    isClearable = false,
    isSearchable = true,
    isLoading = false,
    onInputChange,
    defaultValue = null,
    value = null,
    onChange = () => { },
    options = [],
    menuPlacement = "auto",
    menuIsOpen,
    defaultOptions,
    cacheOptions,
    loadOptions,
    required = false,
    isCreatable = false,
    newOption = null,
    noOptionsAction = null,
    lookLikeInput = false,
    ...selectRest
  },
  ref,
) => {
  /* ----------------------------------------------
     Label props
  ---------------------------------------------- */
  const {
    name: labelName = "",
    id: labelId,
    isHidden = false,
    class: labelClass = "",
  } = label || {};

  /* ----------------------------------------------
     Validation props
  ---------------------------------------------- */
  const {
    isInvalid = false,
    message: errorMessage = "",
    className: validationClass = "",
    name = "",
  } = validation || {};

  /* ----------------------------------------------
     Select Props
  ---------------------------------------------- */
  const {
    className: inputClass = "",
    id: selectId,
    styles: styleOverride = {},
    placeholder = "Select...",
    classNames: classNamesOverride = {},
    components: componentsOverride = {},
  } = selectProps;

  /* ----------------------------------------------
     ID Resolver
  ---------------------------------------------- */
  const autoId = useId();
  const resolvedId = labelId || selectId || autoId;
  const lastInputChangeTime = React.useRef(0);

  /* ----------------------------------------------
     Memoized Overrides
  ---------------------------------------------- */
  const { icon, iconClass = "" } = selectRest;
  const mergedClassNames = useMemo(
    () => ({
      ...customClasses,
      ...classNamesOverride,
      control: (state) => {
        const baseFn = classNamesOverride.control || customClasses.control;
        let classes = baseFn(state);
        if (icon) {
          classes = classes.replace("px-5", "ps-5");
        }
        return classes;
      },
    }),
    [classNamesOverride, icon],
  );

  const mergedStyles = useMemo(
    () => ({
      ...customStyle,
      ...styleOverride,
      control: (base, state) => ({
        ...customStyle.control(base, state),
        ...(styleOverride.control ? styleOverride.control(base, state) : {}),
        paddingRight: icon ? "50px" : undefined,
      }),
    }),
    [styleOverride, icon],
  );

  const mergedComponents = useMemo(
    () => ({
      DropdownIndicator: lookLikeInput ? null : DropIcon,
      LoadingIndicator: null,
      LoadingMessage,
      NoOptionsMessage,
      ClearIndicator,
      MenuList,
      MultiValueRemove,
      IndicatorSeparator: lookLikeInput ? null : undefined,
      ...componentsOverride,
    }),
    [lookLikeInput, componentsOverride],
  );

  const reorderedOptions = useMemo(() => {
    return reorderList(options, value);
  }, [options, value]);

  const reorderedDefaultOptions = useMemo(() => {
    if (Array.isArray(defaultOptions)) {
      return reorderList(defaultOptions, value);
    }
    return defaultOptions;
  }, [defaultOptions, value]);

  return (
    <FormGroup
      className={`form-input-cover  ${className} ${isHorizontal ? `grid align-items-center ${isInvalid ? "mb-3" : ""}` : ""
        }`.trim()}
      style={style}
    >
      {/* Label */}
      {labelName && (
        <FormLabel
          className={`text-primary ${isHorizontal ? "g-col-4 m-0" : ""} ${isHidden ? "visually-hidden" : ""} ${labelClass}`.trim()}
          id={resolvedId}
          labelName={labelName}
          required={required}
        />
      )}

      {/* Input Wrapper */}
      <div
        className={`${isHorizontal ? "g-col-8" : ""
          }  position-relative ${coverClass}  ${isInvalid && errorMessage ? "mb-3" : ""
          }`.trim()}
      >
        <SelectComponent
          ref={ref}
          className={`custom-form-select${inputClass}`}
          classNames={mergedClassNames}
          styles={mergedStyles}
          placeholder={placeholder}
          name={name}
          id={resolvedId}
          isDisabled={isDisabled}
          isLoading={isLoading}
          isClearable={isClearable}
          isSearchable={isSearchable}
          value={value}
          onChange={onChange}
          loadOptions={loadOptions}
          components={mergedComponents}
          options={!loadOptions ? reorderedOptions : undefined}
          menuPlacement={menuPlacement}
          menuIsOpen={menuIsOpen}
          defaultOptions={loadOptions ? reorderedDefaultOptions : undefined}
          cacheOptions={loadOptions ? cacheOptions : undefined}
          isCreatable={isCreatable}
          onInputChange={(val, meta) => {
            if (meta?.action === "input-change" || meta?.action === "set-value") {
              lastInputChangeTime.current = Date.now();
            }
            if (onInputChange) {
              onInputChange(val, meta);
            }
          }}
          filterOption={selectRest.filterOption ?? (onInputChange && !loadOptions ? () => true : undefined)}
          newOption={newOption || noOptionsAction}
          noOptionsAction={noOptionsAction}
          openMenuOnClick={lookLikeInput ? false : selectRest.openMenuOnClick}
          openMenuOnFocus={lookLikeInput ? false : selectRest.openMenuOnFocus}
          {...selectRest}
          onMenuOpen={() => {
            if (Date.now() - lastInputChangeTime.current < 100) {
              return;
            }
            if (selectRest.onMenuOpen) {
              selectRest.onMenuOpen();
            }
          }}
        />

        {/* Custom Icon */}
        {icon && (
          <i
            className={`position-absolute top-0 bottom-0 end-0 flex-center bg-primary-10 primary-text rounded-end-4 ${iconClass}`}
            style={{ width: "40px", pointerEvents: "none", zIndex: 5 }}
          >
            {icon}
          </i>
        )}

        {/* Validation Message */}
        {isInvalid && errorMessage && (
          <FormFeedback
            className={`position-absolute top-100 mt-1 ${validationClass}`}
            message={errorMessage}
          />
        )}
      </div>
    </FormGroup>
  );
};

CustomSelect.displayName = "CustomSelect";

export default forwardRef(CustomSelect);
